/**
 * Alpine.js component for switching between linked Shopify products and managing a product set cart.
 *
 * @param {string[]} sectionIds - An array of section DOM IDs to be replaced when switching products.
 * @returns {object} Alpine.js reactive data object
 */
function linkedProductSwitcher(sectionIds) {
    return {
        /** @type {Array} - List of linked products (i.e., colour variants). */
        linkedProducts: [],

        /** @type {Array} - List of products in the current product set. */
        setProducts: [],

        /** @type {string} - Handle of the currently selected product. */
        currentHandle: '',

        /** @type {string[]} - Section IDs to replace when switching products. */
        sectionIdsToReplace: sectionIds || [],

        /** @type {Map<string, string>} - HTML cache for product pages. */
        pageCache: new Map(),

        /** @type {boolean} - Whether the app is currently loading a product. */
        isLoading: false,

        /** @type {string|null} - Holds any error messages. */
        error: null,

        /** @type {Array} - Line items selected for the cart (variant ID, quantity, etc). */
        selectedItems: [],

        /** @type {string} - The context where this component is used (e.g., 'quick-buy'). */
        context: 'default',

        /**
         * Initialise the component with linked and set product data.
         *
         * @param {object} linkedProductData - Contains linkedProducts and setProducts arrays.
         * @param {string} currentHandle - The handle of the product currently in view.
         * @param {string} productSetId - A unique key for localStorage scoping.
         * @param {string} context - The context where this component is used (e.g., 'quick-buy').
         */
        async init(linkedProductData, currentHandle, productSetId, context) {
            this.currentHandle = currentHandle;
            this.productSetId = productSetId;
            this.context = context || 'default';
            this.localStorageKey = `selectedItems_${productSetId}`;

            if (!linkedProductData?.linkedProducts?.length) return;

            try {
                this.linkedProducts = linkedProductData.linkedProducts;
                this.setProducts = linkedProductData.setProducts;

                // console.table(this.linkedProducts);

                const handles = this.linkedProducts.map(p => p.handle);
                if (window.requestIdleCallback) {
                    requestIdleCallback(() => this.preloadPages(handles));
                } else {
                    // Fallback: defer execution to avoid blocking initial page load
                    setTimeout(() => this.preloadPages(handles), 100);
                }

                const savedItems = localStorage.getItem(this.localStorageKey);
                if (savedItems) {
                    this.selectedItems = JSON.parse(savedItems);
                }

                // Only add popstate listener if not in quick-buy context
                if (this.context !== 'quick-buy') {
                    window.addEventListener('popstate', this.handlePopState.bind(this));
                }
            } catch (error) {
                this.error = `Failed to initialize product switcher: ${error.message}`;
                console.error(error);
            }
        },

        /**
         * Persist the selected items to localStorage.
         */
        saveItems() {
            localStorage.setItem(this.localStorageKey, JSON.stringify(this.selectedItems));
        },

        /**
         * Preload full HTML pages of linked products to cache.
         *
         * @param {string[]} handles - Array of product handles.
         */
        async preloadPages(handles) {
            try {
                await Promise.all(
                    handles.map(async handle => {
                        if (!this.pageCache.has(handle)) {
                            // Deliberately NOT `{ cache: 'force-cache' }`. Storefront pages come
                            // back with no Cache-Control and only `Vary: Accept, accept-encoding`,
                            // so the HTTP cache is keyed on URL alone and cannot tell which theme
                            // produced a response. force-cache returns a stored entry *stale or
                            // fresh, without revalidating*, so a page cached under one theme (an
                            // A/B rollout bucket, or the theme published before the last deploy)
                            // gets spliced into a page rendered by another — see loadProduct's
                            // theme guard for what that breaks. Default caching revalidates.
                            const res = await fetch(`${Shopify.routes.root}products/${handle}`);
                            if (!res.ok) throw new Error(`Failed to preload: ${handle}`);
                            const html = await res.text();
                            this.pageCache.set(handle, html);
                        }
                    })
                );
            } catch (error) {
                console.error('Preload error:', error);
            }
        },

        /**
         * Read the theme id out of a fetched storefront page.
         *
         * Shopify inlines `Shopify.theme = {...,"id":123,...}` via content_for_header, so the id
         * is readable straight off the HTML string — DOMParser won't run the script, so it has to
         * come from the raw text rather than the parsed document.
         *
         * @param {string} htmlString - Raw HTML of a storefront page.
         * @returns {number|null} The theme id, or null if the marker isn't present.
         */
        themeIdFromHtml(htmlString) {
            const match = htmlString.match(/Shopify\.theme\s*=\s*\{[^}]*?"id":\s*(\d+)/);
            return match ? Number(match[1]) : null;
        },

        /**
         * Reorder product options to maintain color-first ordering after DOM replacement.
         */
        reorderProductOptions() {
            // Find all variant picker containers
            const variantPickers = document.querySelectorAll('variant-picker');
            
            variantPickers.forEach(variantPicker => {
                const optionSelectors = variantPicker.querySelectorAll('.product-form__option-selector');
                if (optionSelectors.length <= 1) return; // No need to reorder if 1 or fewer options
                
                // Create arrays to hold different types of options
                const colorOptions = [];
                const sizeOptions = [];
                const otherOptions = [];
                
                // Categorize options
                optionSelectors.forEach(option => {
                    // Check by CSS class rather than text content (since color now shows the actual color value)
                    if (option.classList.contains('product-form__option-selector--colour')) {
                        colorOptions.push(option);
                    } else {
                        const optionName = option.querySelector('.product-form__option-name');
                        if (!optionName) {
                            otherOptions.push(option);
                            return;
                        }
                        
                        const name = optionName.textContent.toLowerCase().replace(':', '').trim();
                        
                        if (name.includes('size')) {
                            sizeOptions.push(option);
                        } else {
                            otherOptions.push(option);
                        }
                    }
                });
                
                // Reorder options: color first, then size, then others
                const reorderedOptions = [...colorOptions, ...sizeOptions, ...otherOptions];
                
                // Update option IDs to maintain proper numbering
                reorderedOptions.forEach((option, index) => {
                    const optionId = option.querySelector('[id*="option-"]');
                    if (optionId) {
                        const newId = optionId.id.replace(/-\d+$/, `-${index + 1}`);
                        optionId.id = newId;
                    }
                    
                    // Update any references to the option ID within the option
                    const optionElements = option.querySelectorAll('[id*="option-"]');
                    optionElements.forEach(el => {
                        if (el.id !== optionId?.id) {
                            el.id = el.id.replace(/-\d+$/, `-${index + 1}`);
                        }
                    });
                });
                
                // Remove all options from the container
                optionSelectors.forEach(option => option.remove());
                
                // Re-append options in the correct order
                reorderedOptions.forEach(option => {
                    variantPicker.appendChild(option);
                });
            });
        },

        /**
         * Replace specific sections of the page with another product's DOM.
         *
         * @param {string} handle - The handle of the new product.
         */
        async loadProduct(handle) {
            if (this.currentHandle === handle || this.isLoading) return;

            this.isLoading = true;
            this.error = null;

            try {
                this.currentHandle = handle;
                
                // Only update page handle if not in quick-buy context
                if (this.context !== 'quick-buy') {
                    history.pushState({ handle }, '', `${Shopify.routes.root}products/${handle}`);
                }

                let htmlString = this.pageCache.get(handle);
                if (!htmlString) {
                    const res = await fetch(`${Shopify.routes.root}products/${handle}`);
                    if (!res.ok) throw new Error(`Failed to load product: ${handle}`);
                    htmlString = await res.text();
                    this.pageCache.set(handle, htmlString);
                }

                // Only splice markup that came from the theme rendering this page. During a theme
                // A/B test (Shopify rollouts) the two themes render different PDP markup for the
                // same ids — the gallery root is `.product__media` on one and `.product__media-alt`
                // on the other, and the alt grid rules only exist in one theme's CSS — so a
                // cross-theme splice leaves the gallery matching neither layout and it collapses to
                // intrinsic size. Ids that exist on only one side (DynamicProductPrice vs
                // DynamicProductPriceMobile/Desktop) also silently stop updating. The same applies
                // to HTML cached before a theme publish. A full navigation always renders
                // consistently, so degrade to that rather than half-applying.
                const fetchedThemeId = this.themeIdFromHtml(htmlString);
                const currentThemeId = window.Shopify?.theme?.id;
                if (fetchedThemeId && currentThemeId && fetchedThemeId !== currentThemeId) {
                    console.warn(
                        `[product-set-switcher] "${handle}" was served by theme ${fetchedThemeId} but this page is theme ${currentThemeId}; falling back to a full navigation.`
                    );
                    this.pageCache.delete(handle);
                    window.location.assign(`${Shopify.routes.root}products/${handle}`);
                    return;
                }

                const oldForm = document.querySelector('form[id^="product-form"]');
                if (oldForm && window._RestockRocket?.teardownAddToCartForm) {
                    window._RestockRocket.teardownAddToCartForm(oldForm);
                }

                const doc = new DOMParser().parseFromString(htmlString, 'text/html');

                // The gallery section runs inside a view transition (named
                // 'product-media' in section-main-product.css). Every other
                // section is swapped *outside* the transition so any
                // Alpine x-for reconciliation (linkedProduct in linkedProducts,
                // etc.) doesn't get captured in the transition snapshots — that
                // was producing visible duplicated swatch items mid-animation.
                const mediaSectionId = 'DynamicProductMedia';
                const scope = (this.context === 'quick-buy' && this.$root) ? this.$root : document;

                const replaceSection = (id) => {
                    const newEl = doc.getElementById(id);
                    if (!newEl) return;
                    scope.querySelectorAll(`#${id}`).forEach(currentEl => {
                        currentEl.outerHTML = newEl.outerHTML;
                    });
                };

                // 1. Swap everything except the gallery, then reorder options.
                this.sectionIdsToReplace
                    .filter(id => id !== mediaSectionId)
                    .forEach(replaceSection);
                this.reorderProductOptions();

                // 2. Let Alpine settle the x-for reconciliation on the new DOM
                //    before we snapshot for the gallery transition. Two rAFs
                //    is the smallest reliable wait across an Alpine tick.
                const isDesktop = window.matchMedia('(min-width: 1000px)').matches;
                const useTransition =
                    isDesktop &&
                    this.context !== 'quick-buy' &&
                    typeof document.startViewTransition === 'function';

                if (useTransition) {
                    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
                    const transition = document.startViewTransition(() => replaceSection(mediaSectionId));
                    try {
                        await transition.finished;
                    } catch (_) {
                        // transition can be skipped/aborted (e.g. by another swatch click);
                        // the swap already happened, so swallow.
                    }
                } else {
                    replaceSection(mediaSectionId);
                }
                
                // Check wishlist status for the new product after DOM is updated
                if (window.checkWishlistForNewProduct) {
                    window.checkWishlistForNewProduct();
                }

                if (window._RestockRocket?.updateProductContext) {
                    window._RestockRocket.updateProductContext(handle).then(success => {
                        if (!success) return;
                        setTimeout(() => {
                            const form = document.querySelector('form[id^="product-form"]');
                            if (form) window._RestockRocket.setupAddToCartForm?.(form);
                            setTimeout(() => {
                                const variantInput = document.querySelector('form[id^="product-form"] [name="id"]');
                                if (variantInput) variantInput.dispatchEvent(new Event('change', { bubbles: true }));
                            }, 150);
                        }, 300);
                    });
                }

            } catch (error) {
                this.error = `Failed to load product: ${error.message}`;
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Handle back/forward navigation between products.
         */
        handlePopState() {
            // Don't handle popstate in quick-buy context
            if (this.context === 'quick-buy') return;
            
            const handle = window.location.pathname.split('/products/')[1];
            if (handle && !handle.includes('/')) {
                this.loadProduct(handle);
            }
        },

        /**
         * Remove a variant from the selection.
         *
         * @param {string} variantId - Shopify variant ID.
         */
        removeFromOrder(variantId) {
            this.selectedItems = this.selectedItems.filter(item => item.id !== variantId);
            this.saveItems();
        },

        /**
         * Change quantity or add new variant to selection.
         */
        changeQuantity(variantId, newQuantity, productTitle, variantTitle) {
            const quantity = parseInt(newQuantity);
            if (isNaN(quantity) || quantity < 1) return;

            const variant = this.setProducts.flatMap(p => p.variants).find(v => v.id == variantId);
            if (!variant) return;

            const existing = this.selectedItems.find(item => item.id === variantId);
            if (existing) {
                existing.quantity = quantity;
            } else {
                this.selectedItems.push({
                    id: variantId,
                    title: productTitle,
                    variant: variantTitle,
                    quantity: quantity,
                    price: variant.price
                });
            }

            this.saveItems();
        },

        increaseQuantity(variantId, productTitle, variantTitle) {
            const item = this.selectedItems.find(item => item.id === variantId);
            this.changeQuantity(variantId, item ? item.quantity + 1 : 1, productTitle, variantTitle);
        },

        decreaseQuantity(variantId, productTitle, variantTitle) {
            const item = this.selectedItems.find(item => item.id === variantId);
            if (!item) return;

            if (item.quantity > 1) {
                this.changeQuantity(variantId, item.quantity - 1, productTitle, variantTitle);
            } else {
                this.removeFromOrder(variantId);
            }
        },

        updateQuantity(variantId, newQuantity, productTitle, variantTitle) {
            this.changeQuantity(variantId, newQuantity, productTitle, variantTitle);
        },

        /**
         * Get current quantity of a variant.
         * @param {string} variantId
         * @returns {number}
         */
        getQty(variantId) {
            const item = this.selectedItems.find(i => i.id === variantId);
            return item ? item.quantity : 0;
        },

        showQtyInput(variantId) {
            return this.getQty(variantId) > 0;
        },

        /**
         * Sum quantities for a product group (e.g. group-type::handle)
         * @param {string} group
         * @returns {number}
         */
        getGroupCount(group) {
            if (!this.setProducts?.length) return 0;

            const [groupType, groupHandle] = group.split('::');

            return this.selectedItems.reduce((sum, item) => {
                const parentProduct = this.setProducts.find(p =>
                    p.variants.some(v => v.id == item.id)
                );

                if (!parentProduct) return sum;

                const parentGroup = `${parentProduct.type}::${parentProduct.handle}`;
                return parentGroup === group ? sum + item.quantity : sum;
            }, 0);
        },

        /**
         * Return the lowest variant price for a grouped product.
         * @param {string} group
         * @returns {number|null}
         */
        getLowestPrice(group) {
            const handle = group.split('::')[1];
            const product = this.setProducts.find(p => p.handle === handle);
            if (!product) return null;

            const lowest = product.variants.reduce((lowest, variant) =>
                !lowest || variant.price < lowest.price ? variant : lowest, null);

            return lowest?.price ?? null;
        },

        /**
         * Format price in GBP for display.
         * @param {number} price - Price in cents/pence
         * @returns {string}
         */
        formatPrice(price) {
            return new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP'
            }).format(price / 100);
        },

        /**
         * Check if a swatch CSS class exists for a colour.
         * @param {string} value
         * @returns {boolean}
         */
        isColorMatch(value) {
            const className = 'bg-' + value.replace(/\s+/g, '_').toLowerCase();
            return document.querySelector('style')?.innerText.includes(className) ?? false;
        },

        /**
         * Get the CSS class for a swatch.
         * @param {string} value
         * @returns {string}
         */
        getSwatchClass(value) {
            return 'bg-' + value.replace(/\s+/g, '_').toLowerCase();
        },

        /**
         * Submit selected variants to Shopify cart.
         */
        async addToCart() {
            if (this.selectedItems.length === 0) return;

            this.isLoading = true;
            this.error = null;

            try {
                const items = this.selectedItems.map(item => ({
                    id: item.id,
                    quantity: item.quantity
                }));

                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ items })
                });

                if (!response.ok) throw new Error('Failed to add items to cart');

                this.selectedItems = [];
                localStorage.removeItem(this.localStorageKey);
                window.location.href = '/cart';
            } catch (error) {
                this.error = `Failed to add items to cart: ${error.message}`;
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        }
    };
}